import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { samplesApi, type Sample } from '../../api/endpoints.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  Dialog,
  SaveErrorNotice,
  TextAreaField,
  saveFieldErrors,
  useToast,
} from '../../components/ui/index.js';

/** The shortest reason the server accepts (the shared `reasonSchema`). */
const MIN_REASON_LENGTH = 3;

/**
 * "Delete Sample" — a VOID, never a deletion (D211, D212).
 *
 * The reason is mandatory and is checked here as a courtesy; the server checks
 * it again, and refuses a sample that is disposed of or that test work still
 * depends on. Whatever the server says is shown in full: the dialog never
 * closes, and the page never changes, unless the void was recorded.
 */
export function SampleVoidDialog({
  sample,
  isOpen,
  onClose,
}: {
  readonly sample: Sample;
  readonly isOpen: boolean;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);

  const voiding = useMutation({
    mutationFn: () => samplesApi.void(sample.id, { reason: reason.trim() }),
    onSuccess: async (saved) => {
      queryClient.setQueryData(['sample', sample.id], saved);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sample', sample.id] }),
        queryClient.invalidateQueries({ queryKey: ['samples'] }),
        queryClient.invalidateQueries({ queryKey: ['sample-receipt'] }),
        queryClient.invalidateQueries({ queryKey: ['sample-receipts'] }),
      ]);
      show({ tone: 'success', message: t('sampleVoid.done', { number: sample.sampleNumber }) });
      close();
    },
  });

  function close() {
    setReason('');
    setReasonError(null);
    voiding.reset();
    onClose();
  }

  function submit() {
    if (reason.trim().length < MIN_REASON_LENGTH) {
      setReasonError(
        reason.trim() === ''
          ? t('sampleVoid.reasonRequired')
          : t('sampleVoid.reasonTooShort', { min: MIN_REASON_LENGTH }),
      );
      return;
    }
    setReasonError(null);
    voiding.mutate();
  }

  const serverReasonError = voiding.error ? saveFieldErrors(voiding.error, t).reason : undefined;
  const fieldError = reasonError ?? serverReasonError;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={close}
      title={t('sampleVoid.title', { number: sample.sampleNumber })}
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={close} isDisabled={voiding.isPending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton priority="destructive" isLoading={voiding.isPending} onPress={submit}>
            {t('sampleVoid.confirm')}
          </ActionButton>
        </ActionBar>
      }
    >
      <div className="flex flex-col gap-stack">
        <Alert tone="warning" title={t('sampleVoid.warningTitle')} description={t('sampleVoid.warningBody')} />
        <TextAreaField
          label={t('sampleVoid.reasonLabel')}
          description={t('sampleVoid.reasonHint')}
          value={reason}
          onChange={(value) => {
            setReason(value);
            if (reasonError) setReasonError(null);
          }}
          isRequired
          rows={3}
          {...(fieldError ? { error: fieldError } : {})}
        />
        {voiding.error ? (
          <SaveErrorNotice
            error={voiding.error}
            title={t('sampleVoid.failed')}
            fieldLabels={{ reason: t('sampleVoid.reasonLabel') }}
          />
        ) : null}
      </div>
    </Dialog>
  );
}
